import { assertBirr, type Birr } from "./money.js";
import { addPeriods, comparePeriods, type Period } from "./period.js";

/**
 * The ledger.
 *
 * Charges increase what a unit owes, credits reduce it, and a unit's balance is
 * the sum of its entries. Nothing stores a balance. Entries are never edited or
 * deleted: a mistake is corrected by posting a reversing entry, which nets the
 * original to zero while both stay visible.
 *
 * A unit never holds a credit. A payment pays owed months oldest first and may
 * leave the last one part-paid; a payment for a month not invoiced yet pulls
 * that invoice forward, in full, rather than sitting as a negative balance.
 * Money beyond what is owed is held for a person to resolve and never enters
 * the ledger.
 */

export type Bucket = "rent" | "contribution";
export type EntryType = "charge" | "credit" | "adjustment";
export type EntrySource = "invoice" | "payment" | "manual";

interface BaseEntry {
  id: string;
  unitId: string;
  termId: string;
  /** Effective date, not the moment the row was written. ISO 8601. */
  date: string;
  type: EntryType;
  /** Signed: positive is owed, negative is paid or forgiven. */
  amount: Birr;
  source: EntrySource;
  createdBy: string;
  reverses?: string | null;
  note?: string;
}

/**
 * Every rent entry belongs to a month, adjustments included.
 *
 * This is not bookkeeping tidiness, it is what keeps the balance correct. A
 * resident is asked to pay per month, so an adjustment that belonged to no
 * month would reduce what the unit owes without reducing what it is asked for,
 * and paying the asked figure would push the balance below zero. Requiring the
 * month makes that unrepresentable rather than merely unlikely, and it also
 * means the accountant has to say which month they are adjusting, which is the
 * question the committee asks anyway.
 */
export interface RentEntry extends BaseEntry {
  bucket: "rent";
  period: Period;
}

/** Contributions answer to a campaign, not to a month. */
export interface ContributionEntry extends BaseEntry {
  bucket: "contribution";
  period: null;
  campaignId: string;
}

export type LedgerEntry = RentEntry | ContributionEntry;

export function isRentEntry(entry: LedgerEntry): entry is RentEntry {
  return entry.bucket === "rent";
}

export type PeriodState = "unpaid" | "partial" | "verified";

export interface PeriodSummary {
  period: Period;
  charged: Birr;
  credited: Birr;
  /** What the month still owes. Never below zero. */
  balance: Birr;
  state: PeriodState;
}

export interface Chargeable {
  period: Period;
  amount: Birr;
  /** False when the invoice has to be created before this can be settled. */
  invoiced: boolean;
}

export class LedgerError extends Error {}

function ofBucket(entries: readonly LedgerEntry[], bucket: Bucket): LedgerEntry[] {
  return entries.filter((entry) => entry.bucket === bucket);
}

function rentEntries(entries: readonly LedgerEntry[]): RentEntry[] {
  return entries.filter(isRentEntry);
}

/** The unit's balance, computed from entries every time. */
export function balanceOf(entries: readonly LedgerEntry[], bucket: Bucket = "rent"): Birr {
  return ofBucket(entries, bucket).reduce((total, entry) => total + entry.amount, 0);
}

/** What a unit has given. Contributions are counted apart from rent, always. */
export function contributed(entries: readonly LedgerEntry[]): Birr {
  return -balanceOf(entries, "contribution");
}

/**
 * Every month the unit has been billed for, oldest first, with what each still
 * owes. Because every rent entry carries a month, these balances add up to the
 * unit's balance exactly.
 */
export function periodSummaries(entries: readonly LedgerEntry[]): PeriodSummary[] {
  const byPeriod = new Map<Period, { charged: Birr; credited: Birr }>();

  for (const entry of rentEntries(entries)) {
    const bucket = byPeriod.get(entry.period) ?? { charged: 0, credited: 0 };
    if (entry.amount >= 0) {
      bucket.charged += entry.amount;
    } else {
      bucket.credited += -entry.amount;
    }
    byPeriod.set(entry.period, bucket);
  }

  return [...byPeriod.entries()]
    .map(([period, { charged, credited }]) => {
      const balance = charged - credited;
      return {
        period,
        charged,
        credited,
        balance: Math.max(balance, 0),
        state: stateOfPeriod(charged, balance),
      };
    })
    .sort((a, b) => comparePeriods(a.period, b.period));
}

function stateOfPeriod(charged: Birr, balance: Birr): PeriodState {
  if (balance <= 0) {
    return "verified";
  }
  return balance < charged ? "partial" : "unpaid";
}

/** Months still owing, oldest first. What a resident is asked to pay. */
export function outstandingPeriods(entries: readonly LedgerEntry[]): Chargeable[] {
  return periodSummaries(entries)
    .filter((summary) => summary.balance > 0)
    .map((summary) => ({ period: summary.period, amount: summary.balance, invoiced: true }));
}

/** The first month that has never been billed. */
export function nextUninvoicedPeriod(
  entries: readonly LedgerEntry[],
  currentPeriod: Period,
): Period {
  const billed = periodSummaries(entries).filter((summary) => summary.charged > 0);
  const last = billed.at(-1);
  if (!last) {
    return currentPeriod;
  }
  return comparePeriods(last.period, currentPeriod) >= 0
    ? addPeriods(last.period, 1)
    : currentPeriod;
}

export interface PaymentPlan {
  lines: Chargeable[];
  total: Birr;
}

/**
 * What a resident is about to pay for. They choose months, never an amount, so
 * the figure is computed here and cannot leave a fraction of a month behind.
 * Arrears come first, then whichever future months they asked for, each of
 * which pulls its invoice forward when the payment is verified.
 */
export function planPayment(options: {
  entries: readonly LedgerEntry[];
  monthlyDue: Birr;
  currentPeriod: Period;
  monthsAhead?: number;
}): PaymentPlan {
  const { entries, monthlyDue, currentPeriod, monthsAhead = 0 } = options;
  assertBirr(monthlyDue, "monthlyDue");
  if (monthlyDue <= 0) {
    throw new LedgerError(`monthlyDue must be positive, received ${monthlyDue}`);
  }
  if (!Number.isInteger(monthsAhead) || monthsAhead < 0) {
    throw new LedgerError(`monthsAhead must not be negative, received ${monthsAhead}`);
  }

  const lines = outstandingPeriods(entries);
  let next = nextUninvoicedPeriod(entries, currentPeriod);
  for (let index = 0; index < monthsAhead; index += 1) {
    lines.push({ period: next, amount: monthlyDue, invoiced: false });
    next = addPeriods(next, 1);
  }

  return { lines, total: lines.reduce((sum, line) => sum + line.amount, 0) };
}

export interface Allocation {
  settled: Chargeable[];
  /** Money beyond what is owed. Becomes held funds, never a credit. */
  remainder: Birr;
}

/**
 * Applies money to months in order, taking each only in full. A month is never
 * partly settled by a payment, which is what keeps a balance from going
 * negative and removes any question of what a leftover figure was meant for.
 */
export function allocate(amount: Birr, lines: readonly Chargeable[]): Allocation {
  assertBirr(amount, "amount");
  if (amount < 0) {
    throw new LedgerError(`amount must not be negative, received ${amount}`);
  }

  const settled: Chargeable[] = [];
  let left = amount;
  for (const line of lines) {
    if (line.amount > left) {
      break;
    }
    settled.push(line);
    left -= line.amount;
  }
  return { settled, remainder: left };
}

export interface Settlement {
  /** Invoices to create because the payment reaches months not yet billed. */
  pullForward: { period: Period; amount: Birr }[];
  /** Credits to post, one per month the money settles. */
  credits: { period: Period; amount: Birr }[];
  /** What is left once every owed month is paid. Becomes a held funds row, not a credit. */
  heldAmount: Birr;
  settledTotal: Birr;
}

/**
 * Everything verifying a payment does to the ledger, worked out before anything
 * is written. The Cloud Function that verifies a payment posts exactly this, in
 * one transaction, so what the resident was shown and what the books record
 * come from the same calculation.
 */
export function settlePayment(options: {
  entries: readonly LedgerEntry[];
  amount: Birr;
  monthlyDue: Birr;
  currentPeriod: Period;
  monthsAhead?: number;
}): Settlement {
  const { entries, amount, monthlyDue, currentPeriod, monthsAhead = 0 } = options;
  const plan = planPayment({ entries, monthlyDue, currentPeriod, monthsAhead });
  const { settled, remainder } = allocate(amount, plan.lines);
  // A resident may pay any amount. What does not cover the next owed month in
  // full goes towards it, so a month can be left part-paid. A month not yet
  // billed is never part-paid: that would be a credit against future rent, so
  // money past what is owed is held for a person to place instead.
  let heldAmount = remainder;
  const next = plan.lines[settled.length];
  if (heldAmount > 0 && next?.invoiced) {
    settled.push({ ...next, amount: heldAmount });
    heldAmount = 0;
  }

  return {
    pullForward: settled
      .filter((line) => !line.invoiced)
      .map((line) => ({ period: line.period, amount: line.amount })),
    credits: settled.map((line) => ({ period: line.period, amount: line.amount })),
    heldAmount,
    settledTotal: settled.reduce((sum, line) => sum + line.amount, 0),
  };
}

/**
 * An adjustment may not take a unit below zero, because that is a credit under
 * another name. The accountant can forgive what is owed, not more than it.
 */
export function adjustmentFits(
  entries: readonly LedgerEntry[],
  amount: Birr,
  bucket: Bucket = "rent",
): boolean {
  assertBirr(amount, "amount");
  return balanceOf(entries, bucket) + amount >= 0;
}

export type UnitState = "unpaid" | "unverified" | "verified" | "partial" | "inactive";

/**
 * What one tile shows for one month. First match wins: a paused unit is not
 * chased, a submission waiting on the accountant outranks whatever the ledger
 * says because it is work for them to do, and the rest follows the month.
 */
export function unitStateFor(options: {
  summary: PeriodSummary | undefined;
  hasPendingPayment: boolean;
  active: boolean;
  billingPaused: boolean;
}): UnitState {
  const { summary, hasPendingPayment, active, billingPaused } = options;
  if (!active || billingPaused) {
    return "inactive";
  }
  if (hasPendingPayment) {
    return "unverified";
  }
  if (!summary || summary.charged === 0) {
    return "unpaid";
  }
  return summary.state;
}

/** How far behind a unit is, in whole months, for the arrears buckets. */
export function monthsBehind(entries: readonly LedgerEntry[]): number {
  return periodSummaries(entries).filter((summary) => summary.balance > 0).length;
}

export interface UnitView {
  balance: Birr;
  /** Whole months still owing. */
  behind: number;
  /** Newest month first, which is the one a resident looks for. */
  months: PeriodSummary[];
  awaiting: Birr;
  held: Birr;
  state: UnitState;
}

/**
 * Everything one unit's screen shows, from its entries.
 *
 * The resident's screen and the accountant's unit sheet are the same numbers
 * seen from two sides, so they are worked out once here. A submitted payment
 * changes the state and nothing else: it is not deducted from the balance and
 * no month is marked paid, because until an accountant confirms the money
 * arrived, the only true thing to say is that it is waiting.
 */
export function unitView(options: {
  entries: readonly LedgerEntry[];
  awaiting: Birr;
  held: Birr;
  active: boolean;
  billingPaused: boolean;
  currentPeriod: Period;
}): UnitView {
  const { entries, awaiting, held, active, billingPaused, currentPeriod } = options;
  assertBirr(awaiting, "awaiting");
  assertBirr(held, "held");

  const months = periodSummaries(entries);

  return {
    balance: balanceOf(entries),
    behind: monthsBehind(entries),
    months: [...months].reverse(),
    awaiting,
    held,
    state: unitStateFor({
      summary: months.find((summary) => summary.period === currentPeriod),
      hasPendingPayment: awaiting > 0,
      active,
      billingPaused,
    }),
  };
}

/**
 * One row on the ledger screen: an entry, and whatever cancelled it.
 *
 * A reversal is a separate append, and the entry it cancels is never written to
 * again by anybody. So "this was reversed" is not a stored fact anywhere. It is
 * derived here, by looking for the entry whose `reverses` names it, which is
 * the only way to know and is also the reason the screen has to do this rather
 * than read a flag.
 */
export interface LedgerLine {
  entry: LedgerEntry;
  /** The entry that cancels this one, when one does. The pair nets to zero. */
  reversal: LedgerEntry | null;
  /**
   * True for a reversal whose target is not in the entries given.
   *
   * It happens on a paged screen, where the original is older than what has
   * loaded. Such a row is shown on its own naming what it cancels, rather than
   * hidden, because an audit trail that quietly drops rows is worse than one
   * that admits it is looking at a window.
   */
  orphan: boolean;
}

/**
 * The entries arranged for reading: newest first, each reversal under its target.
 *
 * A reversal is never listed twice. It appears beneath the entry it cancels, or
 * on its own when that entry is out of range, and never as a row of its own
 * alongside its target.
 */
export function ledgerLines(entries: readonly LedgerEntry[]): LedgerLine[] {
  const reversalOf = new Map<string, LedgerEntry>();
  for (const entry of entries) {
    if (entry.reverses) {
      reversalOf.set(entry.reverses, entry);
    }
  }

  const present = new Set(entries.map((entry) => entry.id));

  /** A reversal whose target is on this page renders under it, not beside it. */
  const paired = (entry: LedgerEntry) =>
    typeof entry.reverses === "string" && present.has(entry.reverses);

  // Newest first, and by id where two share a date, so the order does not
  // change between two reads of the same books.
  const ordered = [...entries].sort(
    (one, two) => two.date.localeCompare(one.date) || two.id.localeCompare(one.id),
  );

  return ordered
    .filter((entry) => !paired(entry))
    .map((entry) => ({
      entry,
      reversal: reversalOf.get(entry.id) ?? null,
      orphan: typeof entry.reverses === "string" && !present.has(entry.reverses),
    }));
}
